/**
 * The public share page: `/s/<id>#<secret>`.
 *
 * The only screen reachable with no session, and the only one that runs without
 * a vault. Everything it needs comes from the fragment.
 *
 * The order of operations is the security-relevant part:
 *
 *   1. derive the bearer token from the fragment (a different HKDF branch from
 *      the key, so handing it to the server reveals nothing)
 *   2. resolve — the server answers `needsPassword` and a salt, and withholds
 *      the wrapped key until a password proof arrives
 *   3. derive the proof and resolve again — now the wrapped key comes back
 *   4. unwrap it with a key derived from the fragment *and* the password, which
 *      the server has never seen either half of
 *
 * Step 2 matters: the wrapped key is not handed to an anonymous caller who has
 * not proved the password, so a leaked link alone gives nothing to grind at
 * offline.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { api, errorText as apiErrorText, type LinkResolveWire } from '../api';
import { ds } from '../ds';
import {
  deriveLinkToken,
  openShareLink,
  parseLinkSecret,
  proveLinkPassword,
  type LinkSecret
} from '../crypto/share';
import { Wordmark } from '../components/Wordmark';
import { unwrapContentKey } from '../crypto/keys';
import { decryptName, decryptMime } from '../crypto/metadata';
import { decryptToBlob, type ChunkContext } from '../crypto/stream';
import { fromBase64 } from '../crypto/primitives';
import { fileIcon, formatBytes, monoPlain, panel, tint } from '../theme';
import { useT } from '../i18n';

type Stage = 'loading' | 'password' | 'ready' | 'error';

export function PublicShare() {
  const t = useT();
  const { Button, Icon, Input } = ds();

  const [stage, setStage] = useState<Stage>('loading');
  const [secret, setSecret] = useState<LinkSecret | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [payload, setPayload] = useState<LinkResolveWire | null>(null);
  const [password, setPassword] = useState('');
  const [proof, setProof] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [mime, setMime] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);

  const open = useCallback(
    async (linkSecret: LinkSecret, bearer: string, passwordProof?: string) => {
      const resolved = await api.links.resolve(bearer, passwordProof);
      setPayload(resolved);

      if (resolved.needsPassword) {
        setStage('password');
        return;
      }
      if (!resolved.node || !resolved.nodeKeyWrapped) {
        throw new Error('That link is no longer available.');
      }

      const nodeKey = await openShareLink(
        linkSecret,
        {
          nodeKeyWrapped: resolved.nodeKeyWrapped,
          passwordSalt: resolved.passwordSalt ?? null,
          kdfParams: resolved.kdfParams ?? null
        },
        password || undefined
      );

      setName(await decryptName(nodeKey, resolved.node.nameEnc));
      setMime(resolved.node.mimeEnc ? await decryptMime(nodeKey, resolved.node.mimeEnc) : null);
      setStage('ready');
      return nodeKey;
    },
    [password]
  );

  useEffect(() => {
    const fragment = window.location.hash.slice(1);
    if (!fragment) {
      setError('This link is missing the part that decrypts it. Copy the whole URL.');
      setStage('error');
      return;
    }

    void (async () => {
      try {
        const linkSecret = parseLinkSecret(fragment);
        const bearer = await deriveLinkToken(linkSecret);
        setSecret(linkSecret);
        setToken(bearer);
        await open(linkSecret, bearer);
      } catch (err) {
        setError(apiErrorText(err, 'That link is not valid.'));
        setStage('error');
      }
    })();
    // Runs once: the fragment does not change without a navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitPassword = async () => {
    if (!secret || !token || !payload?.passwordSalt) return;
    setError(null);
    try {
      const computed = await proveLinkPassword(
        password,
        payload.passwordSalt,
        payload.kdfParams ?? undefined
      );
      setProof(computed);
      await open(secret, token, computed);
    } catch (err) {
      setError(apiErrorText(err, 'That password is not right.'));
    }
  };

  const download = async () => {
    if (!secret || !token || !payload?.node || !payload.revision || !payload.nodeKeyWrapped) return;
    setDownloading(true);
    setError(null);

    try {
      const nodeKey = await openShareLink(
        secret,
        {
          nodeKeyWrapped: payload.nodeKeyWrapped,
          passwordSalt: payload.passwordSalt ?? null,
          kdfParams: payload.kdfParams ?? null
        },
        password || undefined
      );

      if (!payload.node.contentKeyWrapped) throw new Error('That file has no contents.');

      const ctx: ChunkContext = {
        contentKey: await unwrapContentKey(nodeKey, payload.node.contentKeyWrapped),
        revisionId: payload.revision.id,
        nonceBase: fromBase64(payload.revision.nonceBase),
        totalChunks: payload.revision.chunkCount,
        chunkSize: payload.revision.chunkSize
      };

      const blob = await decryptToBlob(
        ctx,
        async (i) => {
          setProgress(Math.round(((i + 1) / ctx.totalChunks) * 100));
          return api.links.chunk(token, i, proof ?? undefined);
        },
        mime ?? 'application/octet-stream'
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(apiErrorText(err, 'Could not decrypt that file. It may have changed.'));
    } finally {
      setDownloading(false);
      setProgress(0);
    }
  };

  const colours = tint(mime);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-page)',
        display: 'grid',
        placeItems: 'center',
        padding: '40px 20px'
      }}
    >
      <div style={{ ...panel, width: '100%', maxWidth: 520, padding: '34px 38px 30px' }}>
        <div style={{ display: 'flex', marginBottom: 22 }}>
          <Wordmark size={19} showMark />
        </div>

        {stage === 'loading' && <span style={monoPlain}>{t('public-share.opening')}</span>}

        {stage === 'password' && (
          <>
            <h1 style={heading}>{t('public-share.this-link-needs-a-password')}</h1>
            <p style={lead}>{t('public-share.whoever-sent-it-should-have')}</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submitPassword();
              }}
            >
              <div style={{ marginTop: 20 }}>
                <Input
                  label={t('public-share.password')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
                />
              </div>
              {error && <p style={errorText}>{error}</p>}
              <div style={{ marginTop: 18 }}>
                <Button variant="primary" size="lg" fullWidth type="submit">
                  {t('public-share.open')}
                </Button>
              </div>
            </form>
          </>
        )}

        {stage === 'ready' && payload?.node && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
              <span
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 14,
                  display: 'grid',
                  placeItems: 'center',
                  background: colours.bg,
                  color: colours.fg
                }}
              >
                <Icon name={fileIcon(payload.node.kind, mime)} size={22} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-strong)' }}>
                  {name}
                </div>
                <div style={monoPlain}>
                  {formatBytes(payload.node.plainSizeBytes)}
                  {payload.role === 'viewer' ? ' · view and download' : ` · ${payload.role}`}
                </div>
              </div>
            </div>

            {error && <p style={errorText}>{error}</p>}

            {payload.node.kind === 'file' ? (
              <Button
                variant="primary"
                size="lg"
                fullWidth
                disabled={downloading}
                onClick={() => void download()}
              >
                {downloading ? `Decrypting… ${progress}%` : 'Download'}
              </Button>
            ) : (
              <p style={lead}>
                {t('public-share.this-is-a-folder-ask')}
              </p>
            )}

          </>
        )}

        {stage === 'error' && (
          <>
            <h1 style={heading}>{t('public-share.this-link-does-not-work')}</h1>
            <p style={lead}>{error}</p>
          </>
        )}
      </div>
    </div>
  );
}

const heading: React.CSSProperties = {
  fontFamily: "var(--font-display), 'Baloo 2', system-ui",
  fontWeight: 800,
  fontSize: 28,
  lineHeight: 1.06,
  letterSpacing: '-.015em',
  color: 'var(--text-strong)',
  margin: 0
};

const lead: React.CSSProperties = {
  fontSize: 14.5,
  lineHeight: 1.6,
  color: 'var(--text-body)',
  margin: '10px 0 0'
};

const errorText: React.CSSProperties = {
  fontSize: 13.5,
  color: 'var(--danger-600)',
  margin: '12px 0 0'
};
